import { useState, useEffect, useCallback } from 'react';
import { API_V1, authFetch, formatAmount } from '../constants';
import PageLayout from '../components/ui/PageLayout';
import KpiGrid from '../components/ui/KpiGrid';
import DataTable, { Column } from '../components/ui/DataTable';
import Modal from '../components/ui/Modal';
import { useGlobalEntity, withEntityParam } from '../hooks/useGlobalEntity';

const API = `${API_V1}/treasury`;

// Phase 10b -- drill-down line-item shape returned by
// /treasury/forecast/week-detail. Each category list shares this shape.
interface ForecastLineItem {
  id: number;
  reference: string;
  counterparty: string;
  due_date: string | null;
  amount: number;
  currency: string;
  entity_code: string | null;
  status: string | null;
  source: string;
  po_number?: string | null;
  frequency?: string | null;
  interest_portion?: number;
  principal_portion?: number;
}

interface WeekDetail {
  week_start: string;
  week_end: string;
  entity_code: string | null;
  totals: {
    ar_inflows: number;
    billing_schedule_inflows?: number;
    ap_outflows: number;
    scheduled_run_outflows: number;
    contract_obligation_outflows: number;
    debt_service_outflows: number;
    lease_payment_outflows?: number;
    insurance_premium_outflows?: number;
  };
  ar_invoices: ForecastLineItem[];
  billing_schedule?: ForecastLineItem[];
  ap_invoices: ForecastLineItem[];
  scheduled_payment_runs: ForecastLineItem[];
  contract_obligations: ForecastLineItem[];
  debt_service: ForecastLineItem[];
  lease_payments?: ForecastLineItem[];
  insurance_premiums?: ForecastLineItem[];
}

// Backend canonical shape — matches services/cash_forecast_service.py:85-95.
// Per Week 1 P6 (Call 7): frontend renamed to backend names; backend is the
// domain-correct contract source. Prior FE used current_cash / weeks / period
// which never matched backend output.
interface ForecastWeek {
  week: number;
  start_date: string;
  end_date: string;
  opening_balance: number;
  ar_inflows: number;
  ap_outflows: number;
  recurring_outflows: number;
  net_change: number;
  closing_balance: number;
  confidence_80?: { low: number; high: number };
  confidence_95?: { low: number; high: number };
}

interface ForecastData {
  generated_at: string;
  current_balance: number;
  forecast_weeks: number;
  min_projected_balance: number;
  breach_week: number | null;
  forecast_method: string;
  history_days: number;
  std_net_change_daily: number;
  weekly: ForecastWeek[];
}

export default function CashForecast() {
  const { entity } = useGlobalEntity();
  const [forecast, setForecast] = useState<ForecastData | null>(null);
  const [loading, setLoading] = useState(true);
  const [weeks, setWeeks] = useState(13);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);

  // Phase 10b -- drill-down state
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState<WeekDetail | null>(null);
  const [detailWeekLabel, setDetailWeekLabel] = useState<string>('');

  const showToast = (msg: string, ok: boolean) => { setToast({ msg, ok }); setTimeout(() => setToast(null), 4000); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(withEntityParam(`${API}/forecast?weeks=${weeks}`, entity));
      if (res.ok) { setForecast(await res.json()); }
      else { showToast('Failed to load forecast', false); }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [weeks, entity]);

  useEffect(() => { load(); }, [load]);

  // Phase 10b -- row click: fetch the categorized week breakdown and
  // open the modal. Mirrors the entity-scope of the parent forecast
  // so per-entity drill-down stays consistent.
  const openWeekDetail = useCallback(async (row: ForecastWeek) => {
    setDetailWeekLabel(`W${row.week} (${row.start_date} - ${row.end_date})`);
    setDetail(null);
    setDetailLoading(true);
    setDetailOpen(true);
    try {
      const url = withEntityParam(
        `${API}/forecast/week-detail?start=${row.start_date}&end=${row.end_date}`,
        entity,
      );
      const res = await authFetch(url);
      if (res.ok) { setDetail(await res.json()); }
      else {
        const e = await res.json().catch(() => ({}));
        showToast(`Drill-down failed (${res.status}): ${e.detail || res.statusText}`, false);
        setDetailOpen(false);
      }
    } catch (e) {
      console.error(e);
      showToast('Drill-down failed', false);
      setDetailOpen(false);
    }
    setDetailLoading(false);
  }, [entity]);

  // KPI totals derived from weekly[] — backend doesn't pre-aggregate.
  const totalInflows = forecast
    ? forecast.weekly.reduce((s, w) => s + (w.ar_inflows || 0), 0)
    : 0;
  const totalOutflows = forecast
    ? forecast.weekly.reduce((s, w) => s + (w.ap_outflows || 0) + (w.recurring_outflows || 0), 0)
    : 0;
  const forecastEnd = forecast && forecast.weekly.length > 0
    ? forecast.weekly[forecast.weekly.length - 1].closing_balance
    : 0;

  const summaryCards = forecast ? [
    { label: 'Current Balance', value: formatAmount(forecast.current_balance), color: 'var(--accent)' },
    { label: 'Forecast End', value: formatAmount(forecastEnd), color: forecastEnd < 0 ? 'var(--negative)' : 'var(--accent)' },
    { label: 'Total Inflows', value: formatAmount(totalInflows), color: 'var(--positive)' },
    { label: 'Total Outflows', value: formatAmount(totalOutflows), color: 'var(--negative)' },
  ] : [];

  const columns: Column[] = [
    { key: 'week', label: 'Week', render: (v: number, row: ForecastWeek) => <span style={{ fontWeight: 600, color: 'var(--accent)' }}>{`W${v} (${row.start_date})`}</span> },
    { key: 'opening_balance', label: 'Opening Balance', align: 'right', render: (v: number) => formatAmount(v) },
    { key: 'ar_inflows', label: 'Inflows', align: 'right', render: (v: number) => <span style={{ color: 'var(--positive)' }}>{formatAmount(v)}</span> },
    { key: 'ap_outflows', label: 'Outflows', align: 'right', render: (_: any, row: ForecastWeek) => { const out = (row.ap_outflows || 0) + (row.recurring_outflows || 0); return <span style={{ color: 'var(--negative)' }}>{formatAmount(out)}</span>; } },
    { key: 'net_change', label: 'Net', align: 'right', render: (v: number) => <span style={{ fontWeight: 600, color: v >= 0 ? 'var(--positive)' : 'var(--negative)' }}>{formatAmount(v)}</span> },
    { key: 'closing_balance', label: 'Closing Balance', align: 'right', render: (v: number) => <span style={{ fontWeight: 600, color: v < 0 ? 'var(--negative)' : 'var(--text-primary)' }}>{formatAmount(v)}</span> },
    // Phase 10b -- visual affordance that rows are clickable.
    { key: 'week', label: '', align: 'right', render: () => <span style={{ color: 'var(--text-hint)', fontSize: 11 }}>Details &rsaquo;</span> },
  ];

  return (
    <PageLayout
      title="Cash Forecast"
      subtitle="13-week rolling cash forecast with inflows, outflows, and projected balances -- click any week for transaction-level drill-down"
    >
      {/* Week selector + refresh */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, alignItems: 'center', marginBottom: 16 }}>
        <select aria-label="Forecast horizon (weeks)" value={weeks} onChange={e => setWeeks(Number(e.target.value))} style={{ padding: '7px 12px', borderRadius: 6, border: '1px solid var(--border-subtle)', fontSize: 13, background: 'var(--bg-card)', color: 'var(--text-primary)', cursor: 'pointer' }}>
          <option value={4}>4 Weeks</option>
          <option value={8}>8 Weeks</option>
          <option value={13}>13 Weeks</option>
          <option value={26}>26 Weeks</option>
        </select>
        <button type="button" onClick={load} style={{ padding: '8px 18px', borderRadius: 6, background: '#1e3a5f', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', border: 'none' }}>Refresh</button>
      </div>

      {forecast && <KpiGrid columns={4} items={summaryCards} />}

      <DataTable
        columns={columns}
        rows={forecast?.weekly || []}
        loading={loading}
        emptyMessage="No forecast data available. Record cash snapshots to generate forecasts."
        onRowClick={openWeekDetail}
      />

      {/* Phase 10b -- per-week drill-down modal */}
      <Modal open={detailOpen} onClose={() => setDetailOpen(false)} title={`Cash Forecast Detail -- ${detailWeekLabel}`} size="lg">
        {detailLoading && <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-secondary)' }}>Loading...</div>}
        {!detailLoading && detail && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <DetailSection
              title="AR Invoices Due (Inflows)"
              items={detail.ar_invoices}
              total={detail.totals.ar_inflows}
              positive
            />
            <DetailSection
              title="Recurring Billing Schedule (Pre-Invoice, Inflows)"
              items={detail.billing_schedule || []}
              total={detail.totals.billing_schedule_inflows || 0}
              positive
            />
            <DetailSection
              title="AP Invoices Due (Outflows)"
              items={detail.ap_invoices}
              total={detail.totals.ap_outflows}
            />
            <DetailSection
              title="Scheduled Payment Runs"
              items={detail.scheduled_payment_runs}
              total={detail.totals.scheduled_run_outflows}
            />
            <DetailSection
              title="Contract Payment Obligations"
              items={detail.contract_obligations}
              total={detail.totals.contract_obligation_outflows}
            />
            <DetailSection
              title="Debt Service (Scheduled Principal + Interest)"
              items={detail.debt_service}
              total={detail.totals.debt_service_outflows}
            />
            <DetailSection
              title="Lease Payments (M18 IFRS 16 / ASC 842)"
              items={detail.lease_payments || []}
              total={detail.totals.lease_payment_outflows || 0}
            />
            <DetailSection
              title="Insurance Premiums"
              items={detail.insurance_premiums || []}
              total={detail.totals.insurance_premium_outflows || 0}
            />
            {/* Empty-state hint when EVERYTHING (including the new
                Phase 10d sources) is empty. */}
            {detail.ar_invoices.length === 0 &&
             (detail.billing_schedule || []).length === 0 &&
             detail.ap_invoices.length === 0 &&
             detail.scheduled_payment_runs.length === 0 &&
             detail.contract_obligations.length === 0 &&
             detail.debt_service.length === 0 &&
             (detail.lease_payments || []).length === 0 &&
             (detail.insurance_premiums || []).length === 0 && (
              <div style={{
                padding: 24, textAlign: 'center',
                color: 'var(--text-secondary)', fontSize: 13,
                background: 'var(--bg-subtle)', borderRadius: 6,
              }}>
                No transactions found in this week across AR / AP / payment runs / contract obligations / debt service / leases / insurance premiums. The forecast row's net change comes from baseline run-rate or aggregated sources not yet decomposed here.
              </div>
            )}
          </div>
        )}
      </Modal>

      {toast && <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 2000, padding: '12px 20px', borderRadius: 8, background: toast.ok ? 'var(--positive-bg)' : 'var(--negative-bg)', color: toast.ok ? 'var(--positive)' : 'var(--negative)', fontSize: 13, fontWeight: 600 }}>{toast.msg}</div>}
    </PageLayout>
  );
}

// Phase 10b -- per-source line-item section inside the drill-down
// modal. Empty sections collapse so the modal stays compact when only
// one or two sources contributed to a given week.
function DetailSection({ title, items, total, positive }: {
  title: string;
  items: ForecastLineItem[];
  total: number;
  positive?: boolean;
}) {
  if (!items || items.length === 0) return null;
  const amountColor = positive ? 'var(--positive)' : 'var(--negative)';
  return (
    <div style={{
      border: '1px solid var(--border-subtle)',
      borderRadius: 6, overflow: 'hidden',
    }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '10px 14px', background: 'var(--bg-subtle)',
        fontSize: 12, fontWeight: 600, color: 'var(--text-primary)',
      }}>
        <span>{title} <span style={{ color: 'var(--text-hint)', fontWeight: 400 }}>({items.length})</span></span>
        <span style={{ color: amountColor }}>{formatAmount(total)}</span>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr style={{ background: 'var(--bg-card)', color: 'var(--text-hint)', textAlign: 'left' }}>
            <th style={{ padding: '8px 14px', fontWeight: 500 }}>Reference</th>
            <th style={{ padding: '8px 14px', fontWeight: 500 }}>Counterparty</th>
            <th style={{ padding: '8px 14px', fontWeight: 500 }}>Due Date</th>
            <th style={{ padding: '8px 14px', fontWeight: 500 }}>Entity</th>
            <th style={{ padding: '8px 14px', fontWeight: 500 }}>Status</th>
            <th style={{ padding: '8px 14px', fontWeight: 500, textAlign: 'right' }}>Amount</th>
          </tr>
        </thead>
        <tbody>
          {items.map(it => (
            <tr key={`${it.source}-${it.id}`} style={{ borderTop: '1px solid var(--border-subtle)' }}>
              <td style={{ padding: '8px 14px', fontFamily: 'monospace', color: 'var(--accent)' }}>{it.reference}</td>
              <td style={{ padding: '8px 14px' }}>{it.counterparty}</td>
              <td style={{ padding: '8px 14px', color: 'var(--text-secondary)' }}>{it.due_date || '-'}</td>
              <td style={{ padding: '8px 14px', color: 'var(--text-secondary)' }}>{it.entity_code || '-'}</td>
              <td style={{ padding: '8px 14px', color: 'var(--text-secondary)' }}>{it.status || '-'}</td>
              <td style={{ padding: '8px 14px', textAlign: 'right', fontWeight: 600, color: amountColor }}>
                {formatAmount(it.amount)}
                {it.currency && it.currency !== 'USD' && (
                  <span style={{ marginLeft: 6, fontSize: 10, color: 'var(--text-hint)', fontWeight: 400 }}>{it.currency}</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
